import { useMemo, useState } from "react";

import Highcharts from "highcharts";

import { Map } from "@similarweb/pro-logic-components";

import { useMapGeoJson } from "app/common/hooks";
import { translate } from "app/filters/i18nFilter";
import { chosenSites } from "app/common/services/chosenSites";
import { CircularProgress } from "pro-features/components/Chart/src/components/annotations/Components/CircularProgress";
import { getHighchartsConfig } from "app/components/TrafficTopCountries/getHighchartsConfig";
import { countryByIdFilter, percentageSignFilter } from "app/filters/ngFilters";
import RegionalsLegendContainer from "app/pages/website-analysis/geography/RegionalsLegendContainer";
import {
    GeoMapContainer,
    GeoMapRootContainer,
    LoaderContainer,
} from "app/pages/website-analysis/geography/StyledComponents";
import type {
    EnrichedMarketShareData,
    MarketShareData,
    MainDomainToRegionalShares,
    DomainShare,
} from "app/pages/website-analysis/geography/types/GeoDataTypes";

import { RegionalsCountryDomainsModal } from "./RegionalsCountryDomainsModal";

const customPieChartFormatter = function () {
    const pointData = this.point.details;
    const isCompare = this.point.isCompare;
    let compareHtml = "";
    if (isCompare) {
        const donutChartId = `donut-chart-${this.point.code}`;

        const legendHTML = `
                <div style="display: flex; width: 170px; flex-direction: column;">
                    <div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-weight: 700; font-size: 12px;">
                        <div>Domain</div>
                        <div>Value</div>
                    </div>
                    ${pointData
                        .sort((a, b) => b.value - a.value)
                        .map(
                            (splitEntry) => `
                            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px;font-size: 12px;font-weight: 500">
                                <div style="display: flex; align-items: center;">
                                    <div style="width: 8px; height: 8px; border-radius: 8px; background-color: ${
                                        splitEntry.color
                                    }; margin-right: 6px;"></div>
                                    <div>${splitEntry.site}</div>
                                </div>
                                <div>${percentageSignFilter()(splitEntry.value, 2)}</div>
                            </div>
                            `,
                        )
                        .join("")}
                </div>
            `;
        setTimeout(() => {
            // @ts-ignore
            Highcharts.chart(donutChartId, {
                chart: {
                    type: "pie",
                    backgroundColor: "transparent",
                    height: 200,
                    width: 200,
                },
                title: { text: "" },
                plotOptions: {
                    pie: {
                        innerSize: "65%",
                        animation: false, // Disable animation
                        dataLabels: {
                            enabled: false,
                        },
                    },
                },
                tooltip: { enabled: false },
                series: [
                    {
                        type: "pie" as const,
                        data: pointData.map((splitEntry) => ({
                            y: splitEntry.value,
                            name: splitEntry.site,
                            color: splitEntry.color,
                        })),
                    },
                ],
                credits: { enabled: false },
                exporting: { enabled: false },
            });
        }, 0);
        compareHtml = `
            <div style="display: flex; flex-direction: row; align-items: flex-start; margin-top: 10px;">
                <div id="${donutChartId}" style="height: 200px; width: 200px;margin-left: -16px"></div>
                <div style="margin-left: 4px; font-size: 12px; display: flex; flex-direction: column; align-items: flex-start;margin-top: 20px">
                    ${legendHTML}
                </div>
            </div>`;
    }
    return `
        <div style="background-color: white; padding: 10px; border-radius: 8px; width: 350px; font-size: 14px;">
            <div class="u-bold">${this.point.name}</div>
            <div style="margin-top: 8px">
                ${translate("common.tables.columns.traffic.share.title")}: <span class="u-bold">${percentageSignFilter()(this.point.value, 2)}</span>
            </div>
            ${compareHtml}
        </div>`;
};

const GEO_SEE_IN_MAP_THRESHOLD = 0.001;

const defaultHighchartsMapTransformer = (isCompare: boolean, data: Array<EnrichedMarketShareData>) => {
    const countryById = countryByIdFilter();
    return data.reduce((acc, item) => {
        // Check if the item's Share is greater than the threshold
        if (item.Share > GEO_SEE_IN_MAP_THRESHOLD) {
            const itemObj = {
                "hc-key": countryById(item.Country).code,
                value: item.Share,
                isCompare: isCompare,
                details: Object.entries(item.ShareList).map(([site, value]) => ({
                    site,
                    value,
                    color: chosenSites.getSiteColor(site),
                })),
            };

            if (isCompare) {
                const topSite = Object.entries(item.ShareList).sort((a, b) => b[1] - a[1])[0][0]; // Assert values as numbers // Sort by value descending // Get the site name of the top entry
                // @ts-ignore
                itemObj.color = chosenSites.getSiteColor(topSite);
            }

            acc.push(itemObj);
        }
        return acc; // Return the accumulator for the next iteration
    }, []); // Start with an empty array
};

const regionalsHighchartsMapTransformer = (isCompare: boolean, data: Array<EnrichedMarketShareData>) => {
    const countryById = countryByIdFilter();
    return data.reduce((acc, item) => {
        const itemObj = {
            "hc-key": countryById(item.Country).code,
            value: item.Share,
            isCompare: isCompare,
            details: Object.entries(item.EnrichedShareList).map(([site, value]) => ({
                site: value.domains.length - 1 > 1 ? `${site} (+${value.domains.length - 1})` : site,
                value: value.aggregatedShare,
                color: chosenSites.getSiteColor(site),
            })),
        };

        if (isCompare) {
            // Sort EnrichedShareList by aggregatedShare in descending order
            const topSite = Object.entries(item.EnrichedShareList).sort(
                (a, b) => b[1].aggregatedShare - a[1].aggregatedShare,
            );

            // Ensure there is at least one entry in the sorted list
            if (topSite.length > 0) {
                // Get the site name (key of the first entry) and set its color
                // @ts-ignore
                itemObj.color = chosenSites.getSiteColor(topSite[0][0]);
            }
        }

        acc.push(itemObj);
        return acc;
    }, []); // Start with an empty array
};

const highchartsMapTransformer = (isRegionalEnabled: boolean, isCompare: boolean, data) => {
    if (isRegionalEnabled) {
        return regionalsHighchartsMapTransformer(isCompare, data);
    }
    return defaultHighchartsMapTransformer(isCompare, data);
};

type ModalDomainData = {
    parentDomain: string | null;
    domains: DomainShare[] | null;
};

interface IGeoMarketShareMapProps {
    countriesTrafficShare: {
        Data: Array<MarketShareData | EnrichedMarketShareData>;
        OverallDomainShare: MainDomainToRegionalShares;
    };
    isLoading?: boolean;
    isCompare?: boolean;
    isRegionalEnabled?: boolean;
}

const GeoMarketShareMap = ({
    countriesTrafficShare,
    isLoading,
    isCompare,
    isRegionalEnabled = false,
}: IGeoMarketShareMapProps) => {
    const { data: mapGeoJson, isLoading: isMapGeoJsonLoading } = useMapGeoJson();
    const mapData = useMemo(() => {
        if (isLoading || !countriesTrafficShare) {
            return [];
        }
        return highchartsMapTransformer(isRegionalEnabled, isCompare, countriesTrafficShare.Data);
    }, [isLoading, countriesTrafficShare, isRegionalEnabled, isCompare]);

    const chartConfig = useMemo(
        () =>
            getHighchartsConfig({
                height: 400,
                formatter: customPieChartFormatter,
            }),
        [],
    );

    const [isDomainsModalOpen, setIsDomainsModalOpen] = useState(false);
    const [modalDomainData, setModalDomainData] = useState<ModalDomainData>({
        parentDomain: null,
        domains: null,
    });

    const handleViewCountryDomainsClick = (parentDomainName: string, domains: Array<DomainShare>) => {
        setModalDomainData({
            parentDomain: parentDomainName,
            domains: domains,
        });
        setIsDomainsModalOpen(true);
    };

    const handleModalClose = () => {
        setModalDomainData({
            parentDomain: null,
            domains: null,
        });
        setIsDomainsModalOpen(false);
    };

    return (
        <GeoMapRootContainer>
            {isLoading ? (
                <LoaderContainer>
                    <CircularProgress />
                </LoaderContainer>
            ) : (
                <>
                    {isRegionalEnabled && countriesTrafficShare ? (
                        <RegionalsLegendContainer
                            data={countriesTrafficShare.OverallDomainShare}
                            onViewCountryDomainsClick={handleViewCountryDomainsClick}
                        />
                    ) : null}
                    <GeoMapContainer>
                        <Map
                            type={isCompare ? "none" : "default"}
                            config={chartConfig}
                            mapData={mapGeoJson}
                            data={mapData}
                            isLoading={isMapGeoJsonLoading}
                            style={{ height: "100%", width: "100%" }}
                        />
                    </GeoMapContainer>
                </>
            )}
            <RegionalsCountryDomainsModal
                parentDomainName={modalDomainData.parentDomain}
                domains={modalDomainData.domains}
                isOpen={isDomainsModalOpen}
                onClose={handleModalClose}
            />
        </GeoMapRootContainer>
    );
};

export default GeoMarketShareMap;
